import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/dealership/lib/api";
import { Panel, Chip } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MoneyField, NumField, MiniToggle, FieldRow } from "@/dealership/components/desk/fields";
import { FREQUENCIES, FREQ_LABEL, money } from "@/dealership/lib/desk";
import { loadDealer, saveDealer, mergeDealer, defaultDealer } from "@/dealership/lib/dealer";
import {
  ArrowLeft, Plus, X, Save, RotateCcw, Receipt, ShieldCheck, Landmark, Grid3x3, Layers, Building2, Sparkles,
} from "lucide-react";

const TERMS = [12, 24, 36, 48, 60, 72, 84, 96];

/** Editable list of plain numbers (grid terms / downs / rates). */
const NumList = ({ values = [], onChange, prefix = "", suffix = "", step = 1, testid }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
    {values.map((v, i) => (
      <div key={i} className="relative w-24">
        <Input
          type="number" step={step} value={v}
          onChange={(e) => onChange(values.map((x, k) => (k === i ? Number(e.target.value) : x)))}
          className="h-9 pr-6 text-right tnum" data-testid={`${testid}-${i}`}
        />
        <button
          type="button" onClick={() => onChange(values.filter((_, k) => k !== i))}
          className="absolute -right-1 -top-1 w-4 h-4 rounded-full bg-card border border-border text-muted-foreground hover:text-red-400 flex items-center justify-center"
          title="Remove" data-testid={`${testid}-remove-${i}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </div>
    ))}
    <Button size="sm" variant="outline" onClick={() => onChange([...values, 0])} data-testid={`${testid}-add`}>
      <Plus className="h-3.5 w-3.5" />
    </Button>
    {(prefix || suffix) && <span className="text-[11px] text-muted-foreground">{prefix}{suffix}</span>}
  </div>
);

export default function DeskSettings() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: loaded } = useQuery({ queryKey: ["desk-settings"], queryFn: async () => (await api.get("/desking/settings")).data });
  const [s, setS] = useState(null);
  const [dealer, setDealer] = useState(loadDealer());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loaded && !s) {
      setS(loaded);
      setDealer(mergeDealer(loadDealer(), loaded.dealer));
    }
  }, [loaded, s]);

  if (!s) return <div className="p-8 text-muted-foreground">Loading desking settings…</div>;

  const patch = (p) => setS((cur) => ({ ...cur, ...p }));
  const patchIn = (key, p) => setS((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), ...p } }));
  const list = (key) => s[key] || [];
  const setList = (key, arr) => patch({ [key]: arr });
  const upd = (key, i, p) => setList(key, list(key).map((x, k) => (k === i ? { ...x, ...p } : x)));
  const rm = (key, i) => setList(key, list(key).filter((_, k) => k !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/desking/settings", { ...s, dealer });
      saveDealer(dealer);
      setS(data);
      qc.setQueryData(["desk-settings"], data);
      toast.success("Desking defaults saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset every desking default back to the factory setup? Saved worksheets are not affected.")) return;
    const { data } = await api.post("/desking/settings/reset");
    setS(data);
    const fresh = mergeDealer(defaultDealer, data.dealer);
    setDealer(fresh);
    saveDealer(fresh);
    qc.setQueryData(["desk-settings"], data);
    toast.success("Defaults restored");
  };

  return (
    <div className="p-6 sm:p-8 pb-20 max-w-6xl" data-testid="desk-settings-page">
      <button onClick={() => navigate("/dealership/desking")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-3" data-testid="desk-settings-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to desking
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl">Desking defaults</h1>
          <p className="text-sm text-muted-foreground mt-2">Every number here is only a starting point — the desk can override anything on a worksheet.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={reset} data-testid="desk-settings-reset"><RotateCcw className="h-4 w-4 mr-2" />Reset</Button>
          <Button onClick={save} disabled={saving} data-testid="desk-settings-save"><Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save defaults"}</Button>
        </div>
      </div>

      <div className="space-y-4">
        <Panel label="Deal defaults" title="Tax, pack & financing start point" icon={Sparkles} testid="desk-settings-defaults">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <FieldRow label="HST rate %">
              <NumField value={s.tax_rate} onChange={(v) => patch({ tax_rate: v })} step={0.01} suffix="%" testid="set-tax-rate" />
            </FieldRow>
            <FieldRow label="Pack" hint="Held back from front gross">
              <MoneyField value={s.pack} onChange={(v) => patch({ pack: v })} testid="set-pack" />
            </FieldRow>
            <FieldRow label="Default APR">
              <NumField value={s.defaults?.apr} onChange={(v) => patchIn("defaults", { apr: v })} step={0.01} suffix="%" testid="set-default-apr" />
            </FieldRow>
            <FieldRow label="Default cash down">
              <MoneyField value={s.defaults?.cash_down} onChange={(v) => patchIn("defaults", { cash_down: v })} testid="set-default-down" />
            </FieldRow>
          </div>
          <div className="mt-4">
            <div className="label-mono mb-2">Default term</div>
            <div className="flex gap-1.5 flex-wrap">
              {TERMS.map((t) => (
                <button key={t} onClick={() => patchIn("defaults", { term_months: t })} data-testid={`set-term-${t}`}
                  className={`px-3 py-1.5 rounded-full border text-xs tnum transition-colors ${
                    Number(s.defaults?.term_months) === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="label-mono mb-2">Default frequency</div>
              <div className="flex gap-1.5 flex-wrap">
                {FREQUENCIES.map((f) => (
                  <button key={f} onClick={() => patchIn("defaults", { frequency: f })} data-testid={`set-freq-${f}`}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                      (s.defaults?.frequency || "biweekly") === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"
                    }`}>{FREQ_LABEL[f]}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="label-mono">Round payments up</span>
              <MiniToggle on={!!s.defaults?.round_payment} onClick={() => patchIn("defaults", { round_payment: !s.defaults?.round_payment })} onLabel="Rounded" offLabel="Exact" testid="set-round-payment" />
            </div>
          </div>
        </Panel>

        <Panel label="Fees" title="Default fee lines" icon={Receipt} testid="desk-settings-fees"
          actions={<Button size="sm" variant="outline" onClick={() => setList("fees", [...list("fees"), { id: `fee_${Date.now()}`, label: "New fee", amount: 0, taxable: true, enabled: true }])} data-testid="set-add-fee"><Plus className="h-3.5 w-3.5 mr-1" />Fee</Button>}>
          <div className="space-y-2">
            {list("fees").map((f, i) => (
              <div key={f.id || i} className="flex items-center gap-2" data-testid={`set-fee-${i}`}>
                <MiniToggle on={f.enabled !== false} onClick={() => upd("fees", i, { enabled: f.enabled === false })} onLabel="On by default" offLabel="Off" testid={`set-fee-enabled-${i}`} />
                <Input value={f.label} onChange={(e) => upd("fees", i, { label: e.target.value })} className="h-9 flex-1" data-testid={`set-fee-label-${i}`} />
                <MiniToggle on={f.taxable !== false} onClick={() => upd("fees", i, { taxable: f.taxable === false })} onLabel="Taxable" offLabel="No tax" testid={`set-fee-taxable-${i}`} />
                <div className="w-28"><MoneyField value={f.amount} onChange={(v) => upd("fees", i, { amount: v })} testid={`set-fee-amount-${i}`} /></div>
                <button onClick={() => rm("fees", i)} className="p-1.5 rounded-full text-muted-foreground hover:text-red-400" title="Remove" data-testid={`set-fee-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </Panel>

        <Panel label="F&I catalog" title="Products, retail & our cost" icon={ShieldCheck} testid="desk-settings-products"
          actions={<Button size="sm" variant="outline" onClick={() => setList("products", [...list("products"), { id: `prod_${Date.now()}`, category: "Protection", name: "New product", provider: "", tier: "", term_months: 0, term_km: 0, price: 0, cost: 0, taxable: true, financed: true }])} data-testid="set-add-product"><Plus className="h-3.5 w-3.5 mr-1" />Product</Button>}>
          <div className="space-y-3">
            {list("products").map((p, i) => (
              <div key={p.id || i} className="rounded-2xl border border-border p-3.5 space-y-2.5" data-testid={`set-product-${i}`}>
                <div className="flex items-center gap-2">
                  <Input value={p.category} onChange={(e) => upd("products", i, { category: e.target.value })} placeholder="Category" className="h-9 w-44 text-xs" data-testid={`set-product-category-${i}`} />
                  <Input value={p.name} onChange={(e) => upd("products", i, { name: e.target.value })} className="h-9 flex-1" data-testid={`set-product-name-${i}`} />
                  <button onClick={() => rm("products", i)} className="p-1.5 rounded-full text-muted-foreground hover:text-red-400" title="Remove" data-testid={`set-product-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input value={p.provider || ""} onChange={(e) => upd("products", i, { provider: e.target.value })} placeholder="Provider" className="h-8 w-40 text-xs" />
                  <Input value={p.tier || ""} onChange={(e) => upd("products", i, { tier: e.target.value })} placeholder="Tier" className="h-8 w-32 text-xs" />
                  <Input type="number" value={p.term_months || ""} onChange={(e) => upd("products", i, { term_months: Number(e.target.value) })} placeholder="mo" className="h-8 w-20 text-xs text-right tnum" />
                  <Input type="number" value={p.term_km || ""} onChange={(e) => upd("products", i, { term_km: Number(e.target.value) })} placeholder="km" className="h-8 w-24 text-xs text-right tnum" />
                  <MiniToggle on={p.taxable !== false} onClick={() => upd("products", i, { taxable: p.taxable === false })} onLabel="Taxable" offLabel="No tax" testid={`set-product-taxable-${i}`} />
                  <MiniToggle on={p.financed !== false} onClick={() => upd("products", i, { financed: p.financed === false })} onLabel="Financed" offLabel="Upfront" testid={`set-product-financed-${i}`} />
                  <div className="w-28"><MoneyField value={p.price} onChange={(v) => upd("products", i, { price: v })} testid={`set-product-price-${i}`} /></div>
                  <div className="w-28"><MoneyField value={p.cost} onChange={(v) => upd("products", i, { cost: v })} testid={`set-product-cost-${i}`} /></div>
                  <span className="text-xs money tnum" data-testid={`set-product-profit-${i}`}>{money((Number(p.price) || 0) - (Number(p.cost) || 0))}</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel label="Lenders" title="Rate tiers by year & term" icon={Landmark} testid="desk-settings-lenders"
          actions={<Button size="sm" variant="outline" onClick={() => setList("lenders", [...list("lenders"), { id: `lender_${Date.now()}`, name: "New lender", tiers: [] }])} data-testid="set-add-lender"><Plus className="h-3.5 w-3.5 mr-1" />Lender</Button>}>
          <div className="space-y-3">
            {list("lenders").map((l, i) => (
              <div key={l.id || i} className="rounded-2xl border border-border p-3.5 space-y-2.5" data-testid={`set-lender-${i}`}>
                <div className="flex items-center gap-2">
                  <Input value={l.name} onChange={(e) => upd("lenders", i, { name: e.target.value })} className="h-9 flex-1 font-heading" data-testid={`set-lender-name-${i}`} />
                  <Button size="sm" variant="ghost" onClick={() => upd("lenders", i, { tiers: [...(l.tiers || []), { label: "New tier", min_year: 0, max_term: 84, rate: 9.99 }] })} data-testid={`set-add-tier-${i}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Tier
                  </Button>
                  <button onClick={() => rm("lenders", i)} className="p-1.5 rounded-full text-muted-foreground hover:text-red-400" title="Remove" data-testid={`set-lender-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
                {(l.tiers || []).map((t, k) => {
                  const setTier = (p) => upd("lenders", i, { tiers: l.tiers.map((x, z) => (z === k ? { ...x, ...p } : x)) });
                  return (
                    <div key={k} className="flex items-center gap-2 flex-wrap pl-1" data-testid={`set-tier-${i}-${k}`}>
                      <Input value={t.label} onChange={(e) => setTier({ label: e.target.value })} placeholder="Tier label" className="h-8 flex-1 min-w-[12rem] text-xs" />
                      <div className="w-24"><NumField value={t.min_year} onChange={(v) => setTier({ min_year: v })} testid={`set-tier-year-${i}-${k}`} /></div>
                      <div className="w-24"><NumField value={t.max_term} onChange={(v) => setTier({ max_term: v })} suffix="mo" testid={`set-tier-term-${i}-${k}`} /></div>
                      <div className="w-24"><NumField value={t.rate} onChange={(v) => setTier({ rate: v })} step={0.01} suffix="%" testid={`set-tier-rate-${i}-${k}`} /></div>
                      <button onClick={() => upd("lenders", i, { tiers: l.tiers.filter((_, z) => z !== k) })} className="p-1.5 rounded-full text-muted-foreground hover:text-red-400" title="Remove tier"><X className="h-3 w-3" /></button>
                    </div>
                  );
                })}
                <p className="text-[11px] text-muted-foreground">Min model year · max term · rate. The desk still picks and overrides the rate on each worksheet.</p>
              </div>
            ))}
          </div>
        </Panel>

        <Panel label="Payment grid" title="Matrix rows & columns" icon={Grid3x3} testid="desk-settings-grid">
          <div className="space-y-5">
            <div>
              <div className="label-mono mb-2">Terms (rows, months)</div>
              <NumList values={s.grid?.terms || []} onChange={(v) => patchIn("grid", { terms: v })} testid="set-grid-terms" />
            </div>
            <div>
              <div className="label-mono mb-2">Down payment columns</div>
              <NumList values={s.grid?.downs || []} onChange={(v) => patchIn("grid", { downs: v })} step={100} testid="set-grid-downs" />
            </div>
            <div>
              <div className="label-mono mb-2">Rate columns (%)</div>
              <NumList values={s.grid?.rates || []} onChange={(v) => patchIn("grid", { rates: v })} step={0.01} testid="set-grid-rates" />
            </div>
            <div>
              <div className="label-mono mb-2">Grid opens on</div>
              <div className="flex gap-2">
                <Chip active={(s.grid?.mode || "down") === "down"} onClick={() => patchIn("grid", { mode: "down" })} testid="set-grid-mode-down">Down payments</Chip>
                <Chip active={s.grid?.mode === "rate"} onClick={() => patchIn("grid", { mode: "rate" })} testid="set-grid-mode-rate">Rates</Chip>
              </div>
            </div>
          </div>
        </Panel>

        <Panel label="Menu selling" title="Protection bundles" icon={Layers} testid="desk-settings-menu"
          actions={<Button size="sm" variant="outline" onClick={() => setList("menu", [...list("menu"), { id: `menu_${Date.now()}`, name: "New bundle", product_ids: [] }])} data-testid="set-add-bundle"><Plus className="h-3.5 w-3.5 mr-1" />Bundle</Button>}>
          <div className="space-y-3">
            {list("menu").map((b, i) => (
              <div key={b.id || i} className="rounded-2xl border border-border p-3.5 space-y-3" data-testid={`set-bundle-${i}`}>
                <div className="flex items-center gap-2">
                  <Input value={b.name} onChange={(e) => upd("menu", i, { name: e.target.value })} className="h-9 w-56 font-heading" data-testid={`set-bundle-name-${i}`} />
                  <span className="text-xs text-muted-foreground tnum">
                    {money((b.product_ids || []).reduce((sum, id) => sum + (Number(list("products").find((p) => p.id === id)?.price) || 0), 0))} retail
                  </span>
                  <button onClick={() => rm("menu", i)} className="ml-auto p-1.5 rounded-full text-muted-foreground hover:text-red-400" title="Remove" data-testid={`set-bundle-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {list("products").map((p) => {
                    const on = (b.product_ids || []).includes(p.id);
                    return (
                      <Chip key={p.id} active={on} testid={`set-bundle-${i}-${p.id}`}
                        onClick={() => upd("menu", i, { product_ids: on ? b.product_ids.filter((x) => x !== p.id) : [...(b.product_ids || []), p.id] })}>
                        {p.name}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel label="Luxury tax" title="Federal select luxury items tax" icon={Sparkles} testid="desk-settings-luxury">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="label-mono">Auto-suggest on the worksheet</span>
              <MiniToggle on={s.luxury_tax?.enabled !== false} onClick={() => patchIn("luxury_tax", { enabled: s.luxury_tax?.enabled === false })} onLabel="On" offLabel="Off" testid="set-lux-enabled" />
            </div>
            <FieldRow label="Threshold">
              <MoneyField value={s.luxury_tax?.threshold} onChange={(v) => patchIn("luxury_tax", { threshold: v })} testid="set-lux-threshold" />
            </FieldRow>
            <FieldRow label="Rate over threshold">
              <NumField value={s.luxury_tax?.rate_over} onChange={(v) => patchIn("luxury_tax", { rate_over: v })} step={0.1} suffix="%" testid="set-lux-over" />
            </FieldRow>
            <FieldRow label="Rate on full price">
              <NumField value={s.luxury_tax?.rate_full} onChange={(v) => patchIn("luxury_tax", { rate_full: v })} step={0.1} suffix="%" testid="set-lux-full" />
            </FieldRow>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">The lesser of the two calculations applies, then HST is charged on top.</p>
        </Panel>

        <Panel label="Quote" title="What the customer sees" icon={Building2} testid="desk-settings-quote">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={dealer.name} onChange={(e) => setDealer({ ...dealer, name: e.target.value })} placeholder="Dealer legal name" className="h-9" data-testid="set-dealer-name" />
              <Input value={dealer.phone} onChange={(e) => setDealer({ ...dealer, phone: e.target.value })} placeholder="Phone" className="h-9" data-testid="set-dealer-phone" />
              <Input value={dealer.address} onChange={(e) => setDealer({ ...dealer, address: e.target.value })} placeholder="Address" className="h-9 sm:col-span-2" data-testid="set-dealer-address" />
              <Input value={dealer.email} onChange={(e) => setDealer({ ...dealer, email: e.target.value })} placeholder="Email" className="h-9" data-testid="set-dealer-email" />
              <Input value={dealer.reg} onChange={(e) => setDealer({ ...dealer, reg: e.target.value })} placeholder="OMVIC / registration #" className="h-9" data-testid="set-dealer-reg" />
              <Input value={dealer.hst_number} onChange={(e) => setDealer({ ...dealer, hst_number: e.target.value })} placeholder="HST #" className="h-9" data-testid="set-dealer-hst" />
              <Input value={dealer.authorized_rep} onChange={(e) => setDealer({ ...dealer, authorized_rep: e.target.value })} placeholder="Authorized representative" className="h-9" data-testid="set-dealer-rep" />
            </div>
            <div>
              <div className="label-mono mb-2">Quote disclaimer</div>
              <Textarea rows={4} value={s.quote_disclaimer || ""} onChange={(e) => patch({ quote_disclaimer: e.target.value })} data-testid="set-disclaimer" />
            </div>
            <p className="text-[11px] text-muted-foreground">Dealer details are shared with the Bill of Sale and payment receipts.</p>
          </div>
        </Panel>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={() => navigate("/dealership/desking")}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="desk-settings-save-bottom"><Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save defaults"}</Button>
      </div>
    </div>
  );
}
